'use client';

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  Space,
  message,
  Tabs,
  Alert,
  Tooltip,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  PlusOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ApiOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Bed24Booking, Bed24BookingStatus } from '@/types';

const { Option } = Select;

// 新增 bed24 過濾器類型
type Bed24Filter = 'all' | 'new' | 'arrivals' | 'departures' | 'current';

// 本地訂單介面，整合 bed24 資料
interface LocalBooking {
  key: string;
  orderId: string;
  bed24BookId?: string; // bed24 訂單 ID
  guestName: string;
  guestPhone: string;
  guestEmail: string;
  roomType: string;
  roomNumber?: string;
  checkIn: string;
  checkOut: string;
  adults: number;
  children: number;
  totalAmount: number;
  paidAmount: number;
  status: 'confirmed' | 'pending' | 'cancelled' | 'completed';
  source: string;
  notes?: string;
  createdAt: string;
  isBed24Sync?: boolean; // 是否來自 bed24 同步
}

const BookingsPageClient: React.FC = () => {
  const [bookings, setBookings] = useState<LocalBooking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<LocalBooking[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBooking, setEditingBooking] = useState<LocalBooking | null>(
    null
  );
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('all');
  const [activeBed24Filter, setActiveBed24Filter] =
    useState<Bed24Filter>('all'); // 新增 bed24 過濾器狀態
  const [syncLoading, setSyncLoading] = useState(false);
  const [bed24Connected, setBed24Connected] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>('');

  // 初始化載入資料
  /**
   * 篩選訂單
   */
  const filterBookings = useCallback(() => {
    let filtered = [...bookings];

    // 先按狀態篩選
    if (activeTab !== 'all') {
      filtered = filtered.filter((b) => b.status === activeTab);
    }

    // 再按 bed24 過濾器篩選
    if (activeBed24Filter !== 'all') {
      const today = dayjs().format('YYYY-MM-DD');
      switch (activeBed24Filter) {
        case 'new':
          // 過去24小時內創建的訂單
          const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
          filtered = filtered.filter(
            (b) => dayjs(b.createdAt).isAfter(yesterday) && b.isBed24Sync
          );
          break;
        case 'arrivals':
          // 今天抵達的訂單
          filtered = filtered.filter(
            (b) => dayjs(b.checkIn).format('YYYY-MM-DD') === today
          );
          break;
        case 'departures':
          // 今天離開的訂單
          filtered = filtered.filter(
            (b) => dayjs(b.checkOut).format('YYYY-MM-DD') === today
          );
          break;
        case 'current':
          // 目前入住的訂單 (入住日期 <= 今天 <= 退房日期)
          filtered = filtered.filter(
            (b) =>
              (dayjs(b.checkIn).isSame(today) ||
                dayjs(b.checkIn).isBefore(today)) &&
              (dayjs(b.checkOut).isSame(today) ||
                dayjs(b.checkOut).isAfter(today))
          );
          break;
      }
    }

    setFilteredBookings(filtered);
  }, [bookings, activeTab, activeBed24Filter]);

  useEffect(() => {
    loadInitialData();
    checkBed24Connection();
  }, []);

  // 當 bookings、activeTab 或 activeBed24Filter 變化時重新篩選
  useEffect(() => {
    filterBookings();
  }, [filterBookings]);

  /**
   * 載入初始化資料
   */
  const loadInitialData = () => {
    // 初始化為空陣列，等待 Bed24 同步
    setBookings([]);
  };

  /**
   * 檢查 bed24 API 連線狀態
   */
  const checkBed24Connection = async () => {
    try {
      const response = await fetch('/api/bed24/test-connection');
      const result = await response.json();
      setBed24Connected(result.success);
      if (!result.success) {
        message.warning('Bed24 API 連線失敗，部分功能可能無法使用');
      }
    } catch (error) {
      setBed24Connected(false);
      console.error('Bed24 連線檢查失敗:', error);
    }
  };

  /**
   * 從 bed24 同步訂單資料 - 根據過濾器類型調用不同的 API
   */
  const syncFromBed24 = async (filter: Bed24Filter = 'all') => {
    setSyncLoading(true);

    // 創建同步記錄
    let syncLogId: number | null = null;
    let params: URLSearchParams;

    try {
      // 根據不同的過濾器設定不同的參數
      switch (filter) {
        case 'new':
          params = new URLSearchParams({
            filter: 'new',
            includeInvoice: 'false',
            includeInfoItems: 'false',
            limit: '100',
          });
          break;
        case 'arrivals':
          params = new URLSearchParams({
            filter: 'arrivals',
            includeInvoice: 'false',
            includeInfoItems: 'false',
            limit: '100',
          });
          break;
        case 'departures':
          params = new URLSearchParams({
            filter: 'departures',
            includeInvoice: 'false',
            includeInfoItems: 'false',
            limit: '100',
          });
          break;
        case 'current':
          params = new URLSearchParams({
            filter: 'current',
            includeInvoice: 'false',
            includeInfoItems: 'false',
            limit: '100',
          });
          break;
        default:
          // 預設獲取過去 30 天到未來 30 天的訂單
          const dateFrom = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
          const dateTo = dayjs().add(30, 'day').format('YYYY-MM-DD');
          params = new URLSearchParams({
            arrivalFrom: dateFrom,
            arrivalTo: dateTo,
            includeInvoice: 'false',
            includeInfoItems: 'false',
            limit: '100',
          });
      }

      // 建立同步記錄
      const syncLogResponse = await fetch('/api/sync-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create',
          sync_type: 'booking',
          sync_source: 'bed24',
          sync_direction: 'pull',
          sync_method: 'manual',
          request_params: Object.fromEntries(params),
        }),
      });

      const syncLogResult = await syncLogResponse.json();
      if (syncLogResult.success) {
        syncLogId = syncLogResult.data.id;
      }

      const response = await fetch(`/api/bed24/bookings?${params}`);
      const result = await response.json();

      if (result.success && result.data && result.data.success) {
        // 處理 Bed24 API 回應結構：result.data.data 才是實際的訂單陣列
        const bookingData = result.data.data || [];
        const bed24Bookings = bookingData.map((booking: Bed24Booking) => ({
          key: `bed24_${booking.bookId}`,
          orderId: booking.sourceRef || booking.bookId,
          bed24BookId: booking.bookId,
          guestName: `${booking.guestFirstName} ${booking.guestName}`,
          guestPhone: booking.guestPhone,
          guestEmail: booking.guestEmail,
          roomType: `房間 ${booking.roomId}`,
          checkIn: booking.arrival,
          checkOut: booking.departure,
          adults: booking.numAdult,
          children: booking.numChild,
          totalAmount: booking.price,
          paidAmount: booking.deposit,
          status: mapBed24Status(booking.status),
          source: booking.source || 'Bed24',
          notes: booking.notes,
          createdAt: booking.created,
          isBed24Sync: true,
        }));

        // 如果是特定過濾器，替換現有數據；否則合併
        if (filter === 'all') {
          // 合併本地訂單和 bed24 訂單
          const existingLocalBookings = bookings.filter((b) => !b.isBed24Sync);
          const mergedBookings = [...existingLocalBookings, ...bed24Bookings];
          setBookings(mergedBookings);
        } else {
          // 特定過濾器，只顯示對應的訂單
          setBookings(bed24Bookings);
        }

        const syncTime = dayjs().format('YYYY-MM-DD HH:mm:ss');
        setLastSyncTime(syncTime);

        // 更新同步記錄
        if (syncLogId) {
          await fetch(`/api/sync-logs/${syncLogId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              status: 'success',
              records_processed: bed24Bookings.length,
              records_updated: bed24Bookings.length,
              records_failed: 0,
              end_time: new Date(),
              response_data: {
                success: true,
                recordCount: bed24Bookings.length,
                syncTime,
              },
            }),
          });
        }

        message.success(
          `成功同步 ${bed24Bookings.length} 筆訂單 (${getFilterLabel(filter)})`
        );
      } else {
        throw new Error(result.message || 'API 回應格式錯誤');
      }
    } catch (error) {
      console.error('Bed24 同步失敗:', error);
      message.error('同步失敗，請檢查網路連線和 API 設定');

      // 更新同步記錄為失敗
      if (syncLogId) {
        await fetch(`/api/sync-logs/${syncLogId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: 'failed',
            records_processed: 0,
            records_updated: 0,
            records_failed: 1,
            end_time: new Date(),
            error_message: error instanceof Error ? error.message : '未知錯誤',
            error_details: error instanceof Error ? error.stack : String(error),
            response_data: {
              success: false,
              error: error instanceof Error ? error.message : '未知錯誤',
            },
          }),
        });
      }
    } finally {
      setSyncLoading(false);
    }
  };

  /**
   * 獲取過濾器標籤
   */
  const getFilterLabel = (filter: Bed24Filter): string => {
    switch (filter) {
      case 'new':
        return '新訂單';
      case 'arrivals':
        return '今日抵達';
      case 'departures':
        return '今日離開';
      case 'current':
        return '目前入住';
      default:
        return '全部';
    }
  };

  /**
   * 處理 bed24 過濾器變更
   */
  const handleBed24FilterChange = (filter: Bed24Filter) => {
    setActiveBed24Filter(filter);
    // 如果選擇了特定過濾器，自動同步對應的數據
    if (filter !== 'all') {
      syncFromBed24(filter);
    }
  };

  /**
   * 將 bed24 狀態轉換為本地狀態
   */
  const mapBed24Status = (
    bed24Status: Bed24BookingStatus
  ): LocalBooking['status'] => {
    switch (bed24Status) {
      case 0:
        return 'cancelled';
      case 1:
        return 'confirmed';
      case 2:
        return 'pending';
      case 3:
        return 'pending';
      case 4:
        return 'cancelled';
      case 5:
        return 'pending';
      default:
        return 'pending';
    }
  };

  /**
   * 根據狀態篩選訂單
   */
  const filterBookingsByStatus = (status: string) => {
    setActiveTab(status);
  };

  /**
   * 搜尋功能
   */
  const handleSearch = (value: string) => {
    if (!value.trim()) {
      filterBookings();
      return;
    }

    const filtered = bookings.filter(
      (booking) =>
        booking.orderId.toLowerCase().includes(value.toLowerCase()) ||
        booking.guestName.toLowerCase().includes(value.toLowerCase()) ||
        booking.guestPhone.includes(value) ||
        booking.guestEmail.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredBookings(filtered);
  };

  /**
   * 表格欄位定義
   */
  const columns = [
    {
      title: '訂單編號',
      dataIndex: 'orderId',
      key: 'orderId',
      render: (text: string, record: LocalBooking) => (
        <div>
          <strong>{text}</strong>
          {record.isBed24Sync && (
            <Tooltip title="來自 Bed24 同步">
              <Tag
                color="blue"
                className="ml-2 text-xs"
              >
                <ApiOutlined /> Bed24
              </Tag>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: '客人資訊',
      key: 'guest',
      render: (record: LocalBooking) => (
        <div>
          <div className="font-medium">{record.guestName}</div>
          <div className="text-gray-500 text-sm">{record.guestPhone}</div>
          <div className="text-gray-500 text-sm">{record.guestEmail}</div>
        </div>
      ),
    },
    {
      title: '房型/房號',
      key: 'room',
      render: (record: LocalBooking) => (
        <div>
          <div>{record.roomType}</div>
          {record.roomNumber && (
            <div className="text-blue-500 text-sm">
              房號: {record.roomNumber}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '入住/退房',
      key: 'dates',
      render: (record: LocalBooking) => (
        <div>
          <div className="text-sm">入住: {record.checkIn}</div>
          <div className="text-sm">退房: {record.checkOut}</div>
        </div>
      ),
    },
    {
      title: '人數',
      key: 'guests',
      render: (record: LocalBooking) =>
        `${record.adults}大${
          record.children > 0 ? `${record.children}小` : ''
        }`,
    },
    {
      title: '金額',
      key: 'amount',
      render: (record: LocalBooking) => (
        <div>
          <div className="font-medium">
            NT$ {record.totalAmount.toLocaleString()}
          </div>
          <div className="text-sm text-gray-500">
            已付: NT$ {record.paidAmount.toLocaleString()}
          </div>
        </div>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          confirmed: { color: 'green', text: '已確認' },
          pending: { color: 'orange', text: '待確認' },
          cancelled: { color: 'red', text: '已取消' },
          completed: { color: 'blue', text: '已完成' },
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '來源',
      dataIndex: 'source',
      key: 'source',
      render: (source: string) => (
        <Tag color={source === '官網' ? 'purple' : 'default'}>{source}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (record: LocalBooking) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            編輯
          </Button>
          {record.status === 'pending' && (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              size="small"
              onClick={() => handleConfirm(record.key)}
            >
              確認
            </Button>
          )}
          <Button
            type="primary"
            danger
            icon={<CloseCircleOutlined />}
            size="small"
            onClick={() => handleCancel(record.key)}
          >
            取消
          </Button>
        </Space>
      ),
    },
  ];

  /**
   * 新增訂單
   */
  const handleAdd = () => {
    setEditingBooking(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  /**
   * 編輯訂單
   */
  const handleEdit = (booking: LocalBooking) => {
    setEditingBooking(booking);
    form.setFieldsValue({
      ...booking,
      checkIn: dayjs(booking.checkIn),
      checkOut: dayjs(booking.checkOut),
    });
    setIsModalVisible(true);
  };

  /**
   * 確認訂單
   */
  const handleConfirm = (key: string) => {
    const updatedBookings = bookings.map((b) =>
      b.key === key ? { ...b, status: 'confirmed' as const } : b
    );
    setBookings(updatedBookings);
    message.success('訂單已確認');
  };

  /**
   * 取消訂單
   */
  const handleCancel = (key: string) => {
    Modal.confirm({
      title: '確認取消訂單',
      content: '您確定要取消這個訂單嗎？此操作無法復原。',
      icon: <ExclamationCircleOutlined />,
      onOk() {
        const updatedBookings = bookings.map((b) =>
          b.key === key ? { ...b, status: 'cancelled' as const } : b
        );
        setBookings(updatedBookings);
        message.success('訂單已取消');
      },
    });
  };

  /**
   * 提交表單
   */
  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      const formattedValues = {
        ...values,
        checkIn: (values.checkIn as dayjs.Dayjs).format('YYYY-MM-DD'),
        checkOut: (values.checkOut as dayjs.Dayjs).format('YYYY-MM-DD'),
      };

      if (editingBooking) {
        const updatedBookings = bookings.map((b) =>
          b.key === editingBooking.key ? { ...b, ...formattedValues } : b
        );
        setBookings(updatedBookings);
        message.success('訂單已更新');
      } else {
        const newBooking: LocalBooking = {
          key: String(Date.now()),
          orderId: `BK${String(Date.now()).slice(-6)}`,
          ...formattedValues,
          createdAt: dayjs().format('YYYY-MM-DD'),
          isBed24Sync: false,
        } as LocalBooking;

        setBookings([...bookings, newBooking]);
        message.success('訂單已新增');
      }

      setIsModalVisible(false);
      form.resetFields();
      setEditingBooking(null);
    } catch (error) {
      console.error('提交表單錯誤:', error);
      message.error('操作失敗，請重試');
    }
  };

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold mb-2">訂單管理</h1>
          <p className="text-gray-600">
            管理所有訂單紀錄，包含 OTA 與官網來源，支援 Bed24 同步
          </p>
        </div>
        <Space>
          <Button
            type="default"
            icon={<ReloadOutlined />}
            onClick={checkBed24Connection}
          >
            檢查連線
          </Button>
          <Button
            type="primary"
            icon={<SyncOutlined />}
            loading={syncLoading}
            onClick={() => syncFromBed24()}
            disabled={!bed24Connected}
          >
            同步 Bed24
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size="large"
          >
            新增訂單
          </Button>
        </Space>
      </div>

      {/* Bed24 連線狀態 */}
      {!bed24Connected && (
        <Alert
          message="Bed24 API 連線異常"
          description="請檢查 API 設定或網路連線。部分功能可能無法使用。"
          type="warning"
          showIcon
          closable
        />
      )}

      {/* 同步資訊 */}
      {lastSyncTime && (
        <Alert
          message={`最後同步時間: ${lastSyncTime}`}
          type="info"
          showIcon
          closable
        />
      )}

      {/* 搜尋和篩選 */}
      <Card>
        <div className="flex justify-between items-center mb-4">
          <Input.Search
            placeholder="搜尋訂單編號、客人姓名、電話或郵件"
            allowClear
            onSearch={handleSearch}
            style={{ width: 400 }}
            prefix={<SearchOutlined />}
          />
          <div className="text-sm text-gray-500">
            共 {bookings.length} 筆訂單
            {bookings.filter((b) => b.isBed24Sync).length > 0 && (
              <span className="ml-2">
                (包含 {bookings.filter((b) => b.isBed24Sync).length} 筆 Bed24
                同步)
              </span>
            )}
          </div>
        </div>

        {/* Bed24 過濾器 */}
        <div className="mb-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <CalendarOutlined />
              <span className="font-medium">Bed24 訂單過濾器：</span>
            </div>
            <Select
              value={activeBed24Filter}
              onChange={handleBed24FilterChange}
              style={{ width: 200 }}
              size="small"
            >
              <Option value="all">
                <UserOutlined /> 全部
              </Option>
              <Option value="new">
                <ClockCircleOutlined /> 新訂單 (24小時內)
              </Option>
              <Option value="arrivals">
                <CalendarOutlined /> 今日抵達
              </Option>
              <Option value="departures">
                <CalendarOutlined /> 今日離開
              </Option>
              <Option value="current">
                <UserOutlined /> 目前入住
              </Option>
            </Select>
          </div>
        </div>

        {/* 狀態標籤頁 */}
        <Tabs
          activeKey={activeTab}
          onChange={filterBookingsByStatus}
          items={[
            {
              key: 'all',
              label: `全部 (${bookings.length})`,
            },
            {
              key: 'pending',
              label: `待確認 (${
                bookings.filter((b) => b.status === 'pending').length
              })`,
            },
            {
              key: 'confirmed',
              label: `已確認 (${
                bookings.filter((b) => b.status === 'confirmed').length
              })`,
            },
            {
              key: 'completed',
              label: `已完成 (${
                bookings.filter((b) => b.status === 'completed').length
              })`,
            },
            {
              key: 'cancelled',
              label: `已取消 (${
                bookings.filter((b) => b.status === 'cancelled').length
              })`,
            },
          ]}
        />

        {/* 訂單表格 */}
        <Table
          columns={columns}
          dataSource={filteredBookings}
          rowKey="key"
          loading={syncLoading}
          pagination={{
            total: filteredBookings.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 筆訂單`,
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 新增/編輯Modal */}
      <Modal
        title={editingBooking ? '編輯訂單' : '新增訂單'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-4"
        >
          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              label="客人姓名"
              name="guestName"
              rules={[{ required: true, message: '請輸入客人姓名' }]}
            >
              <Input placeholder="請輸入客人姓名" />
            </Form.Item>

            <Form.Item
              label="聯絡電話"
              name="guestPhone"
              rules={[{ required: true, message: '請輸入聯絡電話' }]}
            >
              <Input placeholder="請輸入聯絡電話" />
            </Form.Item>

            <Form.Item
              label="電子郵件"
              name="guestEmail"
              rules={[
                { required: true, message: '請輸入電子郵件' },
                { type: 'email', message: '請輸入有效的電子郵件' },
              ]}
            >
              <Input placeholder="請輸入電子郵件" />
            </Form.Item>

            <Form.Item
              label="房型"
              name="roomType"
              rules={[{ required: true, message: '請選擇房型' }]}
            >
              <Select placeholder="請選擇房型">
                <Option value="標準雙人房">標準雙人房</Option>
                <Option value="豪華雙人房">豪華雙人房</Option>
                <Option value="套房">套房</Option>
                <Option value="家庭房">家庭房</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="入住日期"
              name="checkIn"
              rules={[{ required: true, message: '請選擇入住日期' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="退房日期"
              name="checkOut"
              rules={[{ required: true, message: '請選擇退房日期' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="成人數量"
              name="adults"
              rules={[{ required: true, message: '請輸入成人數量' }]}
            >
              <Input
                type="number"
                min={1}
                placeholder="成人數量"
              />
            </Form.Item>

            <Form.Item
              label="兒童數量"
              name="children"
              initialValue={0}
            >
              <Input
                type="number"
                min={0}
                placeholder="兒童數量"
              />
            </Form.Item>

            <Form.Item
              label="總金額"
              name="totalAmount"
              rules={[{ required: true, message: '請輸入總金額' }]}
            >
              <Input
                type="number"
                min={0}
                placeholder="總金額"
              />
            </Form.Item>

            <Form.Item
              label="已付金額"
              name="paidAmount"
              rules={[{ required: true, message: '請輸入已付金額' }]}
            >
              <Input
                type="number"
                min={0}
                placeholder="已付金額"
              />
            </Form.Item>

            <Form.Item
              label="訂單狀態"
              name="status"
              rules={[{ required: true, message: '請選擇訂單狀態' }]}
            >
              <Select placeholder="請選擇訂單狀態">
                <Option value="pending">待確認</Option>
                <Option value="confirmed">已確認</Option>
                <Option value="completed">已完成</Option>
                <Option value="cancelled">已取消</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="訂單來源"
              name="source"
              rules={[{ required: true, message: '請選擇訂單來源' }]}
            >
              <Select placeholder="請選擇訂單來源">
                <Option value="官網">官網</Option>
                <Option value="Booking.com">Booking.com</Option>
                <Option value="Agoda">Agoda</Option>
                <Option value="Expedia">Expedia</Option>
                <Option value="電話預訂">電話預訂</Option>
                <Option value="現場預訂">現場預訂</Option>
              </Select>
            </Form.Item>
          </div>

          <Form.Item
            label="備註"
            name="notes"
          >
            <Input.TextArea
              rows={3}
              placeholder="訂單備註"
            />
          </Form.Item>

          <Divider />

          <Form.Item className="mb-0 text-right">
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>取消</Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<PlusOutlined />}
              >
                {editingBooking ? '更新' : '新增'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BookingsPageClient;
